import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Database, Code, Calendar, Hash, Type, ToggleLeft, FileText,
  MessageSquare, Settings2, History, ChevronDown, ChevronRight, Send, Trash2, User, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const TYPE_ICONS = {
  string: Type, code: Code, date: Calendar, number: Hash, integer: Hash,
  float: Hash, boolean: ToggleLeft, text: FileText,
};
const TYPE_COLORS = {
  string: 'bg-zinc-100 text-zinc-600', code: 'bg-blue-50 text-blue-700',
  date: 'bg-amber-50 text-amber-700', number: 'bg-emerald-50 text-emerald-700',
  integer: 'bg-emerald-50 text-emerald-700', float: 'bg-teal-50 text-teal-700',
  boolean: 'bg-purple-50 text-purple-700', text: 'bg-rose-50 text-rose-600',
};

const DIFF_FIELDS = ['name', 'description', 'data_type', 'length', 'format', 'group_id', 'sort_order'];

function VersionDiff({ older, newer }) {
  if (!older || !newer) return null;
  const os = older.snapshot || {};
  const ns = newer.snapshot || {};
  const diffs = DIFF_FIELDS.filter(f => String(os[f] ?? '') !== String(ns[f] ?? ''));
  if (diffs.length === 0) return <p className="text-xs text-[#9CA3AF] py-2">No field changes detected.</p>;
  return (
    <div className="border border-[#E5E7EB] mt-2" data-testid="version-diff">
      {diffs.map(f => (
        <div key={f} className="grid grid-cols-12 px-3 py-1.5 border-b border-[#E5E7EB] last:border-b-0 text-xs">
          <div className="col-span-2 font-semibold text-[#4B5563]">{f}</div>
          <div className="col-span-5 text-red-600 line-through bg-red-50/50 px-1">{String(os[f] ?? '—')}</div>
          <div className="col-span-5 text-emerald-700 bg-emerald-50/50 px-1">{String(ns[f] ?? '—')}</div>
        </div>
      ))}
    </div>
  );
}

export default function AttributeDetailPage() {
  const { attributeId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('edit_elements');
  const [attr, setAttr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [diffPair, setDiffPair] = useState(null); // [older, newer]
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [allEntities, setAllEntities] = useState([]);
  const [editGroups, setEditGroups] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  const fetchAttr = useCallback(async () => {
    try {
      const r = await api.get(`/attributes/${attributeId}`);
      setAttr(r.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [attributeId]);

  useEffect(() => { fetchAttr(); }, [fetchAttr]);
  useEffect(() => { api.get('/entities').then(r => setAllEntities(r.data)).catch(() => {}); }, []);

  const openEdit = () => {
    setEditForm({
      name: attr.name, description: attr.description || '',
      data_type: attr.data_type, entity_id: attr.entity_id || '',
      group_id: attr.group_id || '__none__',
    });
    if (attr.entity_id) {
      api.get(`/entities/${attr.entity_id}`).then(r => setEditGroups(r.data.groups || [])).catch(() => {});
    }
    setEditOpen(true);
  };

  const handleEditEntityChange = (eid) => {
    setEditForm(p => ({ ...p, entity_id: eid, group_id: '__none__' }));
    api.get(`/entities/${eid}`).then(r => setEditGroups(r.data.groups || [])).catch(() => setEditGroups([]));
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      await api.put(`/attributes/${attributeId}`, {
        name: editForm.name, description: editForm.description,
        data_type: editForm.data_type,
        group_id: editForm.group_id === '__none__' ? null : editForm.group_id,
      });
      // If entity changed, we need a separate mechanism — for now update via the attribute
      // Entity change requires moving schema mappings too, so we handle it carefully
      if (editForm.entity_id !== attr.entity_id) {
        await api.put(`/attributes/${attributeId}`, { name: editForm.name });
        // Note: entity_id change not supported via PUT currently — would need backend update
      }
      toast.success('Attribute updated');
      setEditOpen(false);
      fetchAttr();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setEditSaving(false); }
  };

  const fetchVersions = async () => {
    try {
      const r = await api.get(`/attributes/${attributeId}/versions`);
      setVersions(r.data);
      setVersionsOpen(true);
    } catch (err) { toast.error('Failed to load versions'); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/attributes/${attributeId}/comments`, { text: commentText.trim() });
      setCommentText('');
      fetchAttr();
      toast.success('Comment added');
    } catch (err) { toast.error('Failed to add comment'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/attributes/${attributeId}/comments/${commentId}`);
      fetchAttr();
      toast.success('Comment deleted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const showDiff = (idx) => {
    if (idx >= versions.length - 1) { setDiffPair(null); return; }
    setDiffPair([versions[idx + 1], versions[idx]]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white"><Navbar />
        <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>
      </div>
    );
  }
  if (!attr) {
    return (
      <div className="min-h-screen bg-white"><Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12 text-center text-[#9CA3AF]">Attribute not found</div>
      </div>
    );
  }

  const Icon = TYPE_ICONS[attr.data_type] || Type;
  const colorClass = TYPE_COLORS[attr.data_type] || 'bg-zinc-100 text-zinc-600';

  return (
    <div className="min-h-screen bg-white" data-testid="attribute-detail-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-7 px-2 rounded-none text-[#4B5563]" data-testid="back-btn">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
          </Button>
          {attr.entity_name && (<><span className="text-[#9CA3AF]">/</span><span className="text-[#4B5563]">{attr.entity_name}</span></>)}
          {attr.group_name && (<><span className="text-[#9CA3AF]">/</span><span className="text-[#4B5563]">{attr.group_name}</span></>)}
          <span className="text-[#9CA3AF]">/</span>
          <span className="font-medium text-[#0A0A0A]">{attr.name}</span>
        </div>

        {/* Header */}
        <div className="border-b border-[#E5E7EB] pb-6 mb-6">
          <div className="flex items-start gap-3">
            <Icon className="w-6 h-6 text-[#002FA7] mt-1" strokeWidth={1.5} />
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A] mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{attr.name}</h1>
              <p className="text-sm text-[#4B5563] mb-3">{attr.description}</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs px-2 py-0.5 font-medium ${colorClass}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{attr.data_type}</span>
                {attr.length && <span className="text-xs text-[#9CA3AF]">Length: {attr.length}</span>}
                {attr.format && <span className="text-xs text-[#9CA3AF]">Format: {attr.format}</span>}
                <span className="text-xs text-[#9CA3AF]">v{attr.version || 1}</span>
                {attr.updated_by_name && <span className="text-xs text-[#9CA3AF]">Last edited by {attr.updated_by_name}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 mb-6">
          {canEdit && (
            <>
              <Button variant="outline" size="sm" onClick={openEdit} className="rounded-none text-xs h-8" data-testid="edit-attribute-btn">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Attribute
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/attributes/${attributeId}/applicability`)} className="rounded-none text-xs h-8" data-testid="manage-applicability-btn">
                <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Manage Schema Applicability
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={fetchVersions} className="rounded-none text-xs h-8" data-testid="view-versions-btn">
            <History className="w-3.5 h-3.5 mr-1.5" /> Version History ({attr.version_count || 0})
          </Button>
        </div>

        {/* Metadata grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E5E7EB] border border-[#E5E7EB] mb-8" data-testid="attr-metadata">
          {[
            { label: 'Entity', value: attr.entity_name || '—' },
            { label: 'Group', value: attr.group_name || '—' },
            { label: 'Version', value: `v${attr.version || 1}` },
            { label: 'Created by', value: attr.created_by_name || '—' },
            { label: 'Schemas', value: `${attr.schema_mappings?.length || 0}` },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF] font-semibold mb-1">{label}</div>
              <div className="text-sm font-medium text-[#0A0A0A]">{value}</div>
            </div>
          ))}
        </div>

        {/* Version History (collapsible) */}
        {versionsOpen && versions.length > 0 && (
          <div className="mb-8" data-testid="version-history">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF]">
                <History className="w-3.5 h-3.5 inline mr-1" /> Version History
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setVersionsOpen(false); setDiffPair(null); }} className="text-xs h-6 rounded-none">Close</Button>
            </div>
            <div className="border border-[#E5E7EB]">
              <div className="grid grid-cols-12 bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB]">
                <div className="col-span-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Ver</div>
                <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Changed By</div>
                <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Date</div>
                <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Fields Changed</div>
                <div className="col-span-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Diff</div>
              </div>
              {versions.map((v, idx) => (
                <div key={v.version_id} className={`grid grid-cols-12 items-center px-4 py-2.5 border-b border-[#E5E7EB] last:border-b-0 ${v.is_current ? 'bg-[#002FA7]/5' : ''}`} data-testid={`version-row-${v.version}`}>
                  <div className="col-span-1">
                    <span className="text-xs font-bold text-[#0A0A0A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>v{v.version}</span>
                    {v.is_current && <span className="text-[9px] ml-1 text-[#002FA7] font-semibold">NOW</span>}
                  </div>
                  <div className="col-span-3 flex items-center gap-1.5">
                    <User className="w-3 h-3 text-[#9CA3AF]" strokeWidth={1.5} />
                    <span className="text-xs text-[#4B5563]">{v.changed_by_name || v.changed_by}</span>
                  </div>
                  <div className="col-span-3 text-xs text-[#4B5563]">
                    {v.changed_at ? new Date(v.changed_at).toLocaleString() : '—'}
                  </div>
                  <div className="col-span-3">
                    {v.changed_fields?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {v.changed_fields.map(f => (
                          <span key={f} className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 font-medium">{f}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[10px] text-[#9CA3AF]">{v.is_current ? 'Current' : 'Initial'}</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    {idx < versions.length - 1 && (
                      <Button variant="ghost" size="sm" className="text-[10px] h-6 rounded-none text-[#002FA7]" onClick={() => showDiff(idx)} data-testid={`diff-btn-${v.version}`}>
                        View diff
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {diffPair && <VersionDiff older={diffPair[0]} newer={diffPair[1]} />}
          </div>
        )}

        {/* Schema Mappings */}
        <div className="mb-8">
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">Schema Presence & Valuesets</h2>
          <div className="border border-[#E5E7EB]" data-testid="schema-mappings">
            <div className="grid grid-cols-12 bg-[#F9FAFB] px-4 py-2 border-b border-[#E5E7EB]">
              <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Schema</div>
              <div className="col-span-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Required</div>
              <div className="col-span-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Valueset</div>
              <div className="col-span-5 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Codes</div>
            </div>
            {(attr.schema_mappings || []).map(mapping => (
              <div key={mapping.schema_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#E5E7EB] last:border-b-0 hover:bg-[#F9FAFB]" data-testid={`mapping-${mapping.schema_id}`}>
                <div className="col-span-3 flex items-center gap-2">
                  <Database className="w-3.5 h-3.5 text-[#002FA7]" strokeWidth={1.5} />
                  <button onClick={() => navigate(`/schemas/${mapping.schema_id}`)} className="text-sm text-[#002FA7] hover:underline font-medium">{mapping.schema_name}</button>
                </div>
                <div className="col-span-1 flex items-center">
                  {mapping.is_required ? <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 font-semibold">YES</span> : <span className="text-[10px] text-[#9CA3AF]">No</span>}
                </div>
                <div className="col-span-3 flex items-center">
                  {mapping.valueset ? (
                    <button onClick={() => navigate(`/valuesets/${mapping.valueset_id}`)} className="text-sm text-[#4B5563] hover:text-[#002FA7]">{mapping.valueset.name}</button>
                  ) : <span className="text-xs text-[#9CA3AF]">—</span>}
                </div>
                <div className="col-span-5">
                  {mapping.valueset?.codes?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {mapping.valueset.codes.slice(0, 5).map(c => (
                        <span key={c.code} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-700" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {c.code}: {c.label.length > 20 ? c.label.slice(0, 20) + '...' : c.label}
                        </span>
                      ))}
                      {mapping.valueset.codes.length > 5 && <span className="text-[10px] text-[#9CA3AF]">+{mapping.valueset.codes.length - 5} more</span>}
                    </div>
                  ) : <span className="text-xs text-[#9CA3AF]">Free text</span>}
                </div>
              </div>
            ))}
            {(!attr.schema_mappings || attr.schema_mappings.length === 0) && (
              <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">Not assigned to any schema yet.</div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div data-testid="attr-comments">
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">
            <MessageSquare className="w-3.5 h-3.5 inline mr-1" /> Comments ({attr.comments?.length || 0})
          </h2>

          {/* Add comment form */}
          <div className="flex gap-2 mb-4" data-testid="comment-form">
            <Textarea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              className="rounded-none text-sm flex-1 min-h-[60px]"
              data-testid="comment-input"
              rows={2}
            />
            <Button
              onClick={handleAddComment}
              disabled={submitting || !commentText.trim()}
              className="rounded-none bg-[#0A0A0A] hover:bg-[#171717] self-end"
              data-testid="submit-comment-btn"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>

          {(attr.comments || []).length === 0 ? (
            <div className="border border-dashed border-[#E5E7EB] py-6 text-center text-sm text-[#9CA3AF]">No comments yet.</div>
          ) : (
            <div className="border border-[#E5E7EB]">
              {attr.comments.map(c => (
                <div key={c.comment_id} className="px-4 py-3 border-b border-[#E5E7EB] last:border-b-0" data-testid={`comment-${c.comment_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <User className="w-3 h-3 text-[#9CA3AF]" strokeWidth={1.5} />
                      <span className="text-xs font-medium text-[#0A0A0A]">{c.user_name}</span>
                      <span className="text-[10px] text-[#9CA3AF]">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded-none text-[#9CA3AF] hover:text-red-500" onClick={() => handleDeleteComment(c.comment_id)} data-testid={`delete-comment-${c.comment_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-sm text-[#4B5563] pl-5">{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Attribute Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="edit-attribute-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Edit Attribute</DialogTitle><DialogDescription className="sr-only">Update attribute properties</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs font-semibold text-[#4B5563]">Name</Label><Input value={editForm.name || ''} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className="rounded-none mt-1" data-testid="edit-attr-name" /></div>
            <div><Label className="text-xs font-semibold text-[#4B5563]">Description</Label><Textarea value={editForm.description || ''} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} className="rounded-none mt-1" rows={2} data-testid="edit-attr-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Data Type</Label>
                <Select value={editForm.data_type || 'string'} onValueChange={v => setEditForm(p => ({ ...p, data_type: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-attr-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{['string','date','number','code','text','boolean','integer','float'].map(t => <SelectItem key={t} value={t} className="text-xs font-mono">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Entity</Label>
                <Select value={editForm.entity_id || ''} onValueChange={handleEditEntityChange}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-attr-entity"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{allEntities.map(e => <SelectItem key={e.entity_id} value={e.entity_id} className="text-xs">{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {editGroups.length > 0 && (
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Group</Label>
                <Select value={editForm.group_id || '__none__'} onValueChange={v => setEditForm(p => ({ ...p, group_id: v }))}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-attr-group"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="__none__" className="text-xs">No group</SelectItem>{editGroups.map(g => <SelectItem key={g.group_id} value={g.group_id} className="text-xs">{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={handleEditSave} disabled={editSaving} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-edit-attr-btn">{editSaving ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
